/**
 * Email Broadcast Redux Slice
 * State management for the self-contained email broadcast system
 *
 * Created: January 3, 2026
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "./emailBroadcastApi";

// ==================== ASYNC THUNKS ====================

// Lists
export const fetchEmailListsData = createAsyncThunk(
  "emailBroadcast/fetchEmailLists",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchEmailLists(filters, token);
  }
);

export const fetchEmailListByIdData = createAsyncThunk(
  "emailBroadcast/fetchEmailListById",
  async (payload) => {
    const { id, token } = payload;
    return api.fetchEmailListById(id, token);
  }
);

// Subscribers
export const fetchEmailSubscribersData = createAsyncThunk(
  "emailBroadcast/fetchEmailSubscribers",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchEmailSubscribers(filters, token);
  }
);

export const fetchSubscriberActivityData = createAsyncThunk(
  "emailBroadcast/fetchSubscriberActivity",
  async (payload) => {
    const { subscriberId, token } = payload;
    return api.fetchSubscriberActivity(subscriberId, token);
  }
);

// Templates
export const fetchEmailTemplatesData = createAsyncThunk(
  "emailBroadcast/fetchEmailTemplates",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchEmailTemplates(filters, token);
  }
);

export const fetchEmailTemplateByIdData = createAsyncThunk(
  "emailBroadcast/fetchEmailTemplateById",
  async (payload) => {
    const { id, token } = payload;
    return api.fetchEmailTemplateById(id, token);
  }
);

// Campaigns
export const fetchEmailCampaignsData = createAsyncThunk(
  "emailBroadcast/fetchEmailCampaigns",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchEmailCampaigns(filters, token);
  }
);

export const fetchEmailCampaignByIdData = createAsyncThunk(
  "emailBroadcast/fetchEmailCampaignById",
  async (payload) => {
    const { id, token } = payload;
    return api.fetchEmailCampaignById(id, token);
  }
);

export const fetchEmailCampaignStatsData = createAsyncThunk(
  "emailBroadcast/fetchEmailCampaignStats",
  async (payload) => {
    const { id, token } = payload;
    return api.fetchEmailCampaignStats(id, token);
  }
);

// Analytics
export const fetchEmailAnalyticsData = createAsyncThunk(
  "emailBroadcast/fetchEmailAnalytics",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchEmailAnalytics(filters, token);
  }
);

export const fetchSESQuotaData = createAsyncThunk(
  "emailBroadcast/fetchSESQuota",
  async (payload) => {
    const { token } = payload;
    return api.fetchSESQuota(token);
  }
);

export const fetchQueueStatsData = createAsyncThunk(
  "emailBroadcast/fetchQueueStats",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchQueueStats(filters, token);
  }
);

// Suppression
export const fetchSuppressionListData = createAsyncThunk(
  "emailBroadcast/fetchSuppressionList",
  async (payload) => {
    const { filters, token } = payload;
    return api.fetchSuppressionList(filters, token);
  }
);

// User Types
export const fetchUserTypesData = createAsyncThunk(
  "emailBroadcast/fetchUserTypes",
  async (payload) => {
    const { token } = payload;
    return api.fetchUserTypes(token);
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  // Lists
  emailLists: null,
  emailListsLoading: false,
  emailListsError: null,
  selectedList: null,
  selectedListLoading: false,

  // Subscribers
  emailSubscribers: null,
  emailSubscribersLoading: false,
  emailSubscribersError: null,
  subscriberActivity: null,
  subscriberActivityLoading: false,

  // Templates
  emailTemplates: null,
  emailTemplatesLoading: false,
  emailTemplatesError: null,
  selectedTemplate: null,
  selectedTemplateLoading: false,

  // Campaigns
  emailCampaigns: null,
  emailCampaignsLoading: false,
  emailCampaignsError: null,
  selectedCampaign: null,
  selectedCampaignLoading: false,
  campaignStats: null,
  campaignStatsLoading: false,

  // Analytics
  emailAnalytics: null,
  emailAnalyticsLoading: false,
  emailAnalyticsError: null,

  // SES Quota
  sesQuota: null,
  sesQuotaLoading: false,
  sesQuotaError: null,

  // Queue Stats
  queueStats: null,
  queueStatsLoading: false,
  queueStatsError: null,

  // Suppression List
  suppressionList: null,
  suppressionListLoading: false,
  suppressionListError: null,

  // User Types
  userTypes: null,
  userTypesLoading: false,
};

// ==================== SLICE ====================

const emailBroadcastSlice = createSlice({
  name: "emailBroadcast",
  initialState,
  reducers: {
    clearSelectedList: (state) => {
      state.selectedList = null;
    },
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
    clearSelectedCampaign: (state) => {
      state.selectedCampaign = null;
      state.campaignStats = null;
    },
    clearEmailAnalytics: (state) => {
      state.emailAnalytics = null;
    },
    resetEmailBroadcastState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // ==================== LISTS ====================
      .addCase(fetchEmailListsData.pending, (state) => {
        state.emailListsLoading = true;
        state.emailListsError = null;
      })
      .addCase(fetchEmailListsData.fulfilled, (state, action) => {
        state.emailListsLoading = false;
        state.emailLists = action.payload;
      })
      .addCase(fetchEmailListsData.rejected, (state, action) => {
        state.emailListsLoading = false;
        state.emailListsError = action.error.message;
      })
      .addCase(fetchEmailListByIdData.pending, (state) => {
        state.selectedListLoading = true;
      })
      .addCase(fetchEmailListByIdData.fulfilled, (state, action) => {
        state.selectedListLoading = false;
        state.selectedList = action.payload?.data;
      })
      .addCase(fetchEmailListByIdData.rejected, (state) => {
        state.selectedListLoading = false;
      })

      // ==================== SUBSCRIBERS ====================
      .addCase(fetchEmailSubscribersData.pending, (state) => {
        state.emailSubscribersLoading = true;
        state.emailSubscribersError = null;
      })
      .addCase(fetchEmailSubscribersData.fulfilled, (state, action) => {
        state.emailSubscribersLoading = false;
        state.emailSubscribers = action.payload;
      })
      .addCase(fetchEmailSubscribersData.rejected, (state, action) => {
        state.emailSubscribersLoading = false;
        state.emailSubscribersError = action.error.message;
      })
      .addCase(fetchSubscriberActivityData.pending, (state) => {
        state.subscriberActivityLoading = true;
      })
      .addCase(fetchSubscriberActivityData.fulfilled, (state, action) => {
        state.subscriberActivityLoading = false;
        state.subscriberActivity = action.payload?.data;
      })
      .addCase(fetchSubscriberActivityData.rejected, (state) => {
        state.subscriberActivityLoading = false;
      })

      // ==================== TEMPLATES ====================
      .addCase(fetchEmailTemplatesData.pending, (state) => {
        state.emailTemplatesLoading = true;
        state.emailTemplatesError = null;
      })
      .addCase(fetchEmailTemplatesData.fulfilled, (state, action) => {
        state.emailTemplatesLoading = false;
        state.emailTemplates = action.payload;
      })
      .addCase(fetchEmailTemplatesData.rejected, (state, action) => {
        state.emailTemplatesLoading = false;
        state.emailTemplatesError = action.error.message;
      })
      .addCase(fetchEmailTemplateByIdData.pending, (state) => {
        state.selectedTemplateLoading = true;
      })
      .addCase(fetchEmailTemplateByIdData.fulfilled, (state, action) => {
        state.selectedTemplateLoading = false;
        state.selectedTemplate = action.payload?.data;
      })
      .addCase(fetchEmailTemplateByIdData.rejected, (state) => {
        state.selectedTemplateLoading = false;
      })

      // ==================== CAMPAIGNS ====================
      .addCase(fetchEmailCampaignsData.pending, (state) => {
        state.emailCampaignsLoading = true;
        state.emailCampaignsError = null;
      })
      .addCase(fetchEmailCampaignsData.fulfilled, (state, action) => {
        state.emailCampaignsLoading = false;
        state.emailCampaigns = action.payload;
      })
      .addCase(fetchEmailCampaignsData.rejected, (state, action) => {
        state.emailCampaignsLoading = false;
        state.emailCampaignsError = action.error.message;
      })
      .addCase(fetchEmailCampaignByIdData.pending, (state) => {
        state.selectedCampaignLoading = true;
      })
      .addCase(fetchEmailCampaignByIdData.fulfilled, (state, action) => {
        state.selectedCampaignLoading = false;
        state.selectedCampaign = action.payload?.data;
      })
      .addCase(fetchEmailCampaignByIdData.rejected, (state) => {
        state.selectedCampaignLoading = false;
      })
      .addCase(fetchEmailCampaignStatsData.pending, (state) => {
        state.campaignStatsLoading = true;
      })
      .addCase(fetchEmailCampaignStatsData.fulfilled, (state, action) => {
        state.campaignStatsLoading = false;
        state.campaignStats = action.payload?.data;
      })
      .addCase(fetchEmailCampaignStatsData.rejected, (state) => {
        state.campaignStatsLoading = false;
      })

      // ==================== ANALYTICS ====================
      .addCase(fetchEmailAnalyticsData.pending, (state) => {
        state.emailAnalyticsLoading = true;
        state.emailAnalyticsError = null;
      })
      .addCase(fetchEmailAnalyticsData.fulfilled, (state, action) => {
        state.emailAnalyticsLoading = false;
        state.emailAnalytics = action.payload;
      })
      .addCase(fetchEmailAnalyticsData.rejected, (state, action) => {
        state.emailAnalyticsLoading = false;
        state.emailAnalyticsError = action.error.message;
      })

      // ==================== SES QUOTA ====================
      .addCase(fetchSESQuotaData.pending, (state) => {
        state.sesQuotaLoading = true;
        state.sesQuotaError = null;
      })
      .addCase(fetchSESQuotaData.fulfilled, (state, action) => {
        state.sesQuotaLoading = false;
        state.sesQuota = action.payload?.data;
      })
      .addCase(fetchSESQuotaData.rejected, (state, action) => {
        state.sesQuotaLoading = false;
        state.sesQuotaError = action.error.message;
      })

      // ==================== QUEUE STATS ====================
      .addCase(fetchQueueStatsData.pending, (state) => {
        state.queueStatsLoading = true;
        state.queueStatsError = null;
      })
      .addCase(fetchQueueStatsData.fulfilled, (state, action) => {
        state.queueStatsLoading = false;
        state.queueStats = action.payload?.data;
      })
      .addCase(fetchQueueStatsData.rejected, (state, action) => {
        state.queueStatsLoading = false;
        state.queueStatsError = action.error.message;
      })

      // ==================== SUPPRESSION LIST ====================
      .addCase(fetchSuppressionListData.pending, (state) => {
        state.suppressionListLoading = true;
        state.suppressionListError = null;
      })
      .addCase(fetchSuppressionListData.fulfilled, (state, action) => {
        state.suppressionListLoading = false;
        state.suppressionList = action.payload;
      })
      .addCase(fetchSuppressionListData.rejected, (state, action) => {
        state.suppressionListLoading = false;
        state.suppressionListError = action.error.message;
      })

      // ==================== USER TYPES ====================
      .addCase(fetchUserTypesData.pending, (state) => {
        state.userTypesLoading = true;
      })
      .addCase(fetchUserTypesData.fulfilled, (state, action) => {
        state.userTypesLoading = false;
        state.userTypes = action.payload?.data;
      })
      .addCase(fetchUserTypesData.rejected, (state) => {
        state.userTypesLoading = false;
      });
  },
});

// Export actions
export const {
  clearSelectedList,
  clearSelectedTemplate,
  clearSelectedCampaign,
  clearEmailAnalytics,
  resetEmailBroadcastState,
} = emailBroadcastSlice.actions;

// Export reducer
export default emailBroadcastSlice.reducer;
